import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useContent, useLang } from "../i18n/LanguageContext";
import { useAuth } from "../i18n/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const COUNTRIES = [
  "Deutschland", "Österreich", "Schweiz", "Luxemburg", "Frankreich", "Belgien",
  "Niederlande", "Italien", "Spanien", "Portugal", "Irland", "Dänemark",
  "Schweden", "Finnland", "Polen", "Tschechien", "Liechtenstein", "Andere",
];

const MARBLE = "https://images.unsplash.com/photo-1690049098415-29f96fca22c0?auto=format&fit=crop&w=1400&q=80";

export default function Register() {
  const c = useContent();
  const p = c.portal;
  const { lang } = useLang();
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const prefill = useLocation().state || {};

  const [name, setName] = useState(prefill.name || "");
  const [email, setEmail] = useState(prefill.email || "");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/portal");
  }, [user, navigate]);

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error(p.errGeneric); return; }
    if (!emailValid(email)) { toast.error(p.errEmailInvalid); return; }
    if (password.length < 6) { toast.error(p.errPassword); return; }
    if (!consent) { toast.error(p.errConsent); return; }
    setBusy(true);
    const res = await register({ name, email, password, country, phone, consent, lang });
    setBusy(false);
    if (res.ok) {
      toast.success(p.registerOk);
      const amt = prefill.amount;
      navigate("/portal", amt ? { state: { openDeposit: amt } } : undefined);
    } else if (res.error === "email_exists") {
      toast.error(p.errEmailExists);
    } else {
      toast.error(p.errGeneric);
    }
  };

  const labelCls = "block text-sm font-medium text-[#16233d] mb-2";
  const inputCls =
    "w-full rounded-lg border border-[#e7e2d8] bg-white px-4 py-3.5 text-sm text-[#16233d] placeholder-[#9aa0ad] focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20 transition";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col min-h-screen">
        <div className="flex items-center justify-between px-6 lg:px-14 h-20 shrink-0">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md border border-[#e2d3b3] flex items-center justify-center">
              <span className="font-serif-lux text-xl text-[#b08d57] leading-none">B</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-lux text-lg text-[#16233d] font-semibold">Barlow</div>
              <div className="text-[10px] tracking-[0.18em] text-[#b08d57] uppercase">Consultants SL</div>
            </div>
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 lg:px-14 pb-12">
          <div className="w-full max-w-lg">
            <h1 className="font-serif-lux text-4xl text-[#16233d] font-semibold" data-testid="register-title">{p.registerTitle}</h1>
            <p className="mt-2 text-[#5c6577] text-base">{p.registerSubtitle}</p>

            <Link to="/einlagensicherung" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#faf3e8] px-4 py-2 text-sm font-medium text-[#8f6f3f] hover:text-[#6f5530] transition" data-testid="register-deposit-note">
              <ShieldCheck size={16} className="shrink-0" /> {p.depositNote}
            </Link>

            <form onSubmit={submit} className="mt-7 space-y-5" data-testid="register-form">
              <div>
                <label className={labelCls}>{p.nameLabel}</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="register-name-input" />
              </div>
              <div>
                <label className={labelCls}>{p.emailLabel}</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="register-email-input" />
              </div>
              <div>
                <label className={labelCls}>{p.passwordLabel}</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="register-password-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{p.countryLabel}</label>
                  <select value={country} onChange={(e) => setCountry(e.target.value)} className={`${inputCls} appearance-none ${country ? "text-[#16233d]" : "text-[#9aa0ad]"}`} data-testid="register-country-select">
                    <option value="">{p.countryPlaceholder}</option>
                    {COUNTRIES.map((c2) => (<option key={c2} value={c2} className="text-[#16233d]">{c2}</option>))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{p.phoneLabel}</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} data-testid="register-phone-input" />
                </div>
              </div>

              <label className="flex items-start gap-3 pt-1 cursor-pointer" data-testid="register-consent-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#c9bfa6] accent-[#b08d57] focus:ring-[#b08d57]" data-testid="register-consent-checkbox" />
                <span className="text-sm leading-relaxed text-[#5c6577]">
                  {p.consentIntro}{" "}
                  <Link to="/agb" className="text-[#b08d57] hover:text-[#8f6f3f]">{p.consentTerms}</Link>,{" "}
                  <Link to="/agb" className="text-[#b08d57] hover:text-[#8f6f3f]">{p.consentAgb}</Link>{" "}
                  {p.consentMid}{" "}
                  <Link to="/datenschutz" className="text-[#b08d57] hover:text-[#8f6f3f]">{p.consentPrivacy}</Link>.
                </span>
              </label>

              <button type="submit" disabled={busy} className="btn-gold rounded-lg w-full py-4 font-medium disabled:opacity-60" data-testid="register-submit-button">
                {p.registerBtn}
              </button>
            </form>

            <div className="mt-5 text-sm text-[#5c6577]">
              {p.haveAccount}{" "}
              <Link to="/login" className="font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors" data-testid="register-to-login">{p.toLogin}</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img src={MARBLE} alt="Barlow Consultants" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#16233d]/75 via-[#16233d]/20 to-transparent" />
        <div className="absolute bottom-14 left-14 right-14">
          <h2 className="font-serif-lux text-4xl leading-tight text-white">{c.finalCta.title}</h2>
        </div>
      </div>
    </div>
  );
}
